/**
 * Marketing Start — matriz comercial (produção + automação/e-mail da planilha).
 * Taxa da planilha automação: 4× mensal = 7h48 → 1,95h/unidade.
 * Taxa disparos: 5× mensal = 9h45 → 1,95h/unidade.
 */

export const MARKETING_START_NAME = 'Marketing Start'

/** Horas por unidade (7:48 ÷ 4 e 9:45 ÷ 5). */
export const MARKETING_START_HOURS_PER_AUTOMATION = (7 + 48 / 60) / 4
export const MARKETING_START_HOURS_PER_EMAIL_DISPATCH = (9 + 45 / 60) / 5

export const MARKETING_START_AUTOMATION_QTY = 2
export const MARKETING_START_EMAIL_QTY = 3

export const MARKETING_START_CATEGORIES = {
  trafego: 'Gestão de Tráfego e Performance',
  crm: 'CRM & Automação de E-mail',
  acompanhamento: 'Acompanhamento Estratégico',
  suporte: 'Suporte Visual e Gestão',
} as const

const MARKETING_START_BOTTOM_ROW = { x: 38, y: 56.5 } as const
const MARKETING_START_ACOMPANHAMENTO = { x: 68, y: 56.5 } as const

/** Layout 2×2: tráfego/suporte col1 (38%), CRM/acompanhamento col2 (68%), grade Y 10% / 56.5%. */
export const MARKETING_START_CATEGORY_COORDS = {
  [MARKETING_START_CATEGORIES.trafego]: { x: 36, y: 10 },
  [MARKETING_START_CATEGORIES.crm]: { x: 68, y: 10 },
  [MARKETING_START_CATEGORIES.acompanhamento]: MARKETING_START_ACOMPANHAMENTO,
  [MARKETING_START_CATEGORIES.suporte]: MARKETING_START_BOTTOM_ROW,
  /** Legado Plano START — mesmo bloco 16 (Suporte Visual). */
  'Criação, Visual e Gestão': MARKETING_START_BOTTOM_ROW,
} as const

/** Slide completo — blocos fixos + categorias (export e preview). */
export const MARKETING_START_PROPOSAL_LAYOUT = {
  leftBlockWidth: 35,
  titleY: 7.1,
  projectTypeY: 16.4,
  descriptionY: 27.3,
  bonusY: 48,
  priceY: 82.6,
  paymentY: 90,
  loyaltyY: 74.6,
  planTitleY: 2.7,
  serviceBlockX: 38,
  serviceBlockGap: 68,
  serviceBlockStartY: 10,
  categoryCoords: { ...MARKETING_START_CATEGORY_COORDS },
} as const

const automationHours =
  Math.round(MARKETING_START_AUTOMATION_QTY * MARKETING_START_HOURS_PER_AUTOMATION * 100) / 100
const emailHours =
  Math.round(MARKETING_START_EMAIL_QTY * MARKETING_START_HOURS_PER_EMAIL_DISPATCH * 100) / 100

export const marketingStartDeliverables = [
  {
    name: 'Configuração e manutenção de plataformas de tráfego',
    category: MARKETING_START_CATEGORIES.trafego,
    quantity: 'Sob demanda',
    hours: 0.82,
  },
  {
    name: 'Gerenciamento de Campanhas recorrentes de tráfego pago (Até 2 campanhas simultâneas)',
    category: MARKETING_START_CATEGORIES.trafego,
    quantity: '2',
    hours: 1.63,
  },
  {
    name: 'Criação e gerenciamento de campanhas sazonais em plataformas de tráfego (Até 1 campanha mensal)',
    category: MARKETING_START_CATEGORIES.trafego,
    quantity: '1',
    hours: 1.02,
  },
  {
    name: 'Card estático para anúncios (4 cards - até 2 variações por campanha - mensais)',
    category: MARKETING_START_CATEGORIES.trafego,
    quantity: '4',
    hours: 10,
  },
  {
    name: 'Carrosséis para anúncios (Até 1 criação Feed e Stories)',
    category: MARKETING_START_CATEGORIES.trafego,
    quantity: '1',
    hours: 2.5,
  },
  {
    name: 'Gerenciamento de automações',
    category: MARKETING_START_CATEGORIES.crm,
    quantity: String(MARKETING_START_AUTOMATION_QTY),
    hours: automationHours,
  },
  {
    name: 'Disparos E-mail marketing',
    category: MARKETING_START_CATEGORIES.crm,
    quantity: String(MARKETING_START_EMAIL_QTY),
    hours: emailHours,
  },
  {
    name: 'Reunião de alinhamento e relatório de resultados (Mensal)',
    category: MARKETING_START_CATEGORIES.acompanhamento,
    quantity: '1',
    hours: 1.92,
  },
  {
    name: 'Análise, atendimento e consultoria (Semanal)',
    category: MARKETING_START_CATEGORIES.acompanhamento,
    quantity: 'Semanal',
    hours: 3.73,
  },
  {
    name: 'Criação de artes digitais extras (Banners para site, Card, Storie, entre outros) (Até 3 mensais)',
    category: MARKETING_START_CATEGORIES.suporte,
    quantity: '3',
    hours: 7.05,
  },
  {
    name: 'Gestão de site ou redes sociais: atualização de bio, banners/capas, vitrines, promos, cupons (Sob demanda)',
    category: MARKETING_START_CATEGORIES.suporte,
    quantity: 'Sob demanda',
    hours: 1.4,
  },
] as const

function buildServicesFromDeliverables(
  deliverables: ReadonlyArray<{
    name: string
    category: string
    quantity: string
    hours?: number
  }>,
) {
  const byCategory = new Map<
    string,
    Array<{ name: string; isIncluded: boolean; quantity: string; hours: number }>
  >()
  for (const d of deliverables) {
    const list = byCategory.get(d.category) ?? []
    list.push({
      name: d.name,
      isIncluded: true,
      quantity: d.quantity,
      hours: Number(d.hours ?? 0),
    })
    byCategory.set(d.category, list)
  }
  return Array.from(byCategory.entries()).map(([category, services]) => ({ category, services }))
}

export type MarketingStartConfigOptions = {
  baseValue?: number
  hourlyRate?: number
  showBonus?: boolean
  bonus?: string
}

export function buildMarketingStartConfig(options: MarketingStartConfigOptions = {}) {
  const deliverables = marketingStartDeliverables.map((d) => ({ ...d }))
  const totalHours = Math.round(deliverables.reduce((acc, d) => acc + d.hours, 0) * 100) / 100
  const baseValue = options.baseValue ?? 3899
  const hourlyRate =
    options.hourlyRate ??
    Math.round((baseValue / totalHours) * 100) / 100

  return {
    projectType: 'Plano de Marketing',
    showBonus: options.showBonus ?? false,
    bonus: options.bonus ?? 'Diagnóstico Inicial de SEO Grátis',
    duration: 6,
    hourlyRate,
    totalHours,
    suggestedValue: baseValue,
    pricing: {
      entry: baseValue,
      total: baseValue * 6,
      installments: 6,
      installmentValue: baseValue,
      recorrente: true,
      duracao: 6,
    },
    services: buildServicesFromDeliverables(deliverables),
    deliverables,
    proposalLayout: { ...MARKETING_START_PROPOSAL_LAYOUT },
  }
}

export const marketingStartPlanSeed = {
  name: MARKETING_START_NAME,
  description:
    'Ideal para empresas iniciando em tráfego pago, criativos, automações e e-mail marketing com acompanhamento mensal.',
  baseValue: 3899,
  category: 'Marketing',
  type: 'RECORRENTE' as const,
  isActive: true,
  config: buildMarketingStartConfig({ baseValue: 3899 }),
}
